import type {
    Content,
    ContentSalesforceObjectsAssociation,
    GetContentSalesforceObjectAssociationsRequest,
} from "@amzn/ads-blink-client";
import { ContentType } from "@amzn/ads-blink-client";
import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";

import { getClientId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { authStore } from "../../store/auth";
import type { AssociatedPitchedContent } from "../../store/editContent";
import { notificationStore, NotificationType } from "../../store/notification";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

const logAndNotifyError = (contextMessage: string, error: unknown) => {
    console.error(contextMessage, error);
    notificationStore
        .getState()
        .upsertNotification(
            `${contextMessage}. Please try again, or contact support.`,
            NotificationType.ERROR,
        );
};

export function usePitchedContentsAction(): [
    AsyncState<AssociatedPitchedContent[]>,
    UseAsyncActions<AssociatedPitchedContent[]>,
] {
    const accountId = getAdvertiserId();

    const [contentsState, contentsAction] = useAsync<
        AssociatedPitchedContent[]
    >(async () => {
        const response = await fetchPitchedContentFromACC();
        const notInProgressContents: PitchedContent[] = response.contents ?? [];

        return handleSfActivityWorkflow(notInProgressContents, accountId);
    }, []);

    useAsyncErrorStatusNotification(contentsState);

    return [contentsState, contentsAction];
}

const handleSfActivityWorkflow = async (
    notInProgressContents: PitchedContent[],
    accountId?: string,
): Promise<AssociatedPitchedContent[]> => {
    const contentsNeedingAssociations: Content[] = notInProgressContents.map(
        mapPitchedContentToContent,
    );

    let associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();
    if (contentsNeedingAssociations.length) {
        const associationResponse = await fetchSfAssociationsFromPLM(
            contentsNeedingAssociations,
        );

        associationsMap = buildAssociationsMap(associationResponse, accountId);
    }

    const mappedContents = attachAssociations(
        notInProgressContents,
        associationsMap,
    );

    const withFilteredOpps = mappedContents.map((content) =>
        filterContentForAccount(content, accountId),
    );
    return excludeIfNoOppAndNoAssoc(withFilteredOpps);
};

const attachAssociations = (
    pitchedContents: PitchedContent[], // From ACC
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>, // From PLM
): AssociatedPitchedContent[] => {
    return pitchedContents.map((pitchedContent) => {
        const contentId = pitchedContent.contentId;
        const salesforceAssociations = associationsMap.get(contentId!) ?? [];
        return {
            ...pitchedContent,
            associations: salesforceAssociations,
        };
    });
};

const filterContentForAccount = (
    content: AssociatedPitchedContent,
    accountId?: string,
): AssociatedPitchedContent => {
    if (accountId && content.accountOpportunities) {
        const filteredOpps = content.accountOpportunities.filter(
            (opportunity) => opportunity.accountId === accountId,
        );
        return { ...content, accountOpportunities: filteredOpps };
    }
    return content;
};

const excludeIfNoOppAndNoAssoc = (
    contents: AssociatedPitchedContent[],
): AssociatedPitchedContent[] => {
    return contents.filter((c) => {
        const isSalesforceAssociationsEmpty = isEmpty(c.associations);
        return !isSalesforceAssociationsEmpty;
    });
};

const buildAssociationsMap = (
    response: {
        contentSalesforceObjectAssociations?: ContentSalesforceObjectsAssociation[];
    },
    accountId?: string,
): Map<string, ContentSalesforceObjectsAssociation[]> => {
    const associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();

    response.contentSalesforceObjectAssociations
        ?.filter(
            (
                item,
            ): item is ContentSalesforceObjectsAssociation & {
                contentId: string;
            } =>
                !!item.content?.contentId && accountId !== undefined
                    ? item.salesforceAssociation?.salesforceAccountId ===
                      accountId
                    : false,
        )
        .forEach((item) => {
            const contentId = item.content?.contentId;
            const existing = associationsMap.get(contentId!) ?? [];
            existing.push(item);
            associationsMap.set(contentId!, existing);
        });
    return associationsMap;
};

const mapPitchedContentToContent = (
    pitchedContent: PitchedContent,
): Content => {
    const { contentId, contentType } = pitchedContent;
    const validContentTypes = Object.values(ContentType);

    if (contentType === "PITCH") {
        return {
            contentId,
            contentType: ContentType.PITCHED_CONTENT,
        };
    }
    if (validContentTypes.includes(contentType as ContentType)) {
        return {
            contentId,
            contentType: contentType as ContentType,
        };
    }
    return {
        contentId,
        contentType: undefined,
    };
};

const fetchSfAssociationsFromPLM = async (contents: Content[]) => {
    try {
        const request: GetContentSalesforceObjectAssociationsRequest = {
            userAlias: authStore.getState().username,
            clientId: getClientId(),
            contents,
        };
        const response = await (
            await getBlinkApiClientV2()
        ).getContentSalesforceObjectAssociations(request);

        return {
            contentSalesforceObjectAssociations:
                response.contentSalesforceObjectAssociations ?? [],
        };
    } catch (error) {
        logAndNotifyError(
            "Error fetching Salesforce associations from PLM",
            error,
        );
        return {
            contentSalesforceObjectAssociations: [],
        };
    }
};

const fetchPitchedContentFromACC = async () => {
    try {
        return await (await getDiscoveryApiClient()).getPitchedContent();
    } catch (error) {
        logAndNotifyError("Error fetching pitched content from ACC", error);
        return { contents: [] };
    }
};
